import { Layout, RefreshCw } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { SearchInput } from "../ui/search-input";
import { ResizablePanel } from "../ui/resizable";
import { WidgetItem } from "./WidgetItem";

interface Widget {
  _id: string;
  displayName: string;
  slug: string;
  widgetKind?: string;
  description?: string;
}

interface WidgetsSidebarProps {
  widgets: Widget[];
  filteredWidgets: Widget[];
  selectedWidgetId?: string;
  fetchingWidgets: boolean;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  onRefresh: () => void;
  onSelectWidget: (widgetId: string) => void;
}

export function WidgetsSidebar({
  widgets,
  filteredWidgets,
  selectedWidgetId,
  fetchingWidgets,
  searchQuery,
  onSearchQueryChange,
  onRefresh,
  onSelectWidget,
}: WidgetsSidebarProps) {
  return (
    <ResizablePanel defaultSize={35} minSize={20} maxSize={55}>
      <div className="h-full flex flex-col border-r border-border bg-background">
        <div className="px-4 py-4 border-b border-border bg-background space-y-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layout className="h-3 w-3 text-muted-foreground" />
              <h2 className="text-xs font-semibold text-foreground">Widgets</h2>
              <Badge variant="secondary" className="text-xs font-mono">
                {widgets.length}
              </Badge>
            </div>
            <Button
              onClick={onRefresh}
              variant="ghost"
              size="sm"
              disabled={fetchingWidgets}
              title="Refresh"
            >
              <RefreshCw
                className={`h-3 w-3 ${fetchingWidgets ? "animate-spin" : ""} cursor-pointer`}
              />
            </Button>
          </div>
          <SearchInput
            value={searchQuery}
            onValueChange={onSearchQueryChange}
            placeholder="Search widgets by name or description"
          />
        </div>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-2 pb-16">
              {fetchingWidgets ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center mb-3">
                    <RefreshCw className="h-4 w-4 text-muted-foreground animate-spin cursor-pointer" />
                  </div>
                  <p className="text-xs text-muted-foreground font-semibold mb-1">
                    Loading widgets...
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    Fetching widgets from database
                  </p>
                </div>
              ) : filteredWidgets.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    {widgets.length === 0
                      ? "No widgets found. Try syncing from MCP to populate the catalog."
                      : "No widgets match your search."}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {filteredWidgets.map((widget) => (
                    <WidgetItem
                      key={widget._id}
                      widget={widget}
                      isSelected={selectedWidgetId === widget._id}
                      onClick={() => onSelectWidget(widget._id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </ResizablePanel>
  );
}
